// ============================================================================
// BrightPath - NotificationPreferences Component
// BP-12-03: Notifications Frontend
// ============================================================================

import React, { useState } from 'react';
import {
  Bell,
  Mail,
  Smartphone,
  MessageSquare,
  Moon,
  Save,
  Loader2,
} from 'lucide-react';
import {
  NotificationCategory,
  NOTIFICATION_CATEGORY_CONFIG,
} from '../types/notificationsTypes';
import type {
  NotificationPreferencesProps,
  NotificationPreference,
  GlobalNotificationSettings,
  UpdatePreferencesRequest,
  UpdateGlobalSettingsRequest,
} from '../types/notificationsTypes';

// ----------------------------------------------------------------------------
// Toggle Switch Component
// ----------------------------------------------------------------------------

interface ToggleProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

const Toggle: React.FC<ToggleProps> = ({
  enabled,
  onChange,
  disabled = false,
  size = 'md',
}) => {
  const sizeClasses = {
    sm: 'w-8 h-4',
    md: 'w-10 h-5',
  };

  const dotSizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
  };

  const translateClasses = {
    sm: enabled ? 'translate-x-4' : 'translate-x-0.5',
    md: enabled ? 'translate-x-5' : 'translate-x-0.5',
  };

  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      disabled={disabled}
      className={`
        relative inline-flex items-center
        ${sizeClasses[size]}
        rounded-full transition-colors duration-200
        focus:outline-none focus:ring-2 focus:ring-sage focus:ring-offset-2
        ${enabled ? 'bg-sage' : 'bg-warm-gray/30'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
      role="switch"
      aria-checked={enabled}
    >
      <span
        className={`
          ${dotSizeClasses[size]}
          rounded-full bg-white shadow-sm
          transform transition-transform duration-200
          ${translateClasses[size]}
        `}
      />
    </button>
  );
};

// ----------------------------------------------------------------------------
// Category Preference Row
// ----------------------------------------------------------------------------

interface CategoryRowProps {
  preference: NotificationPreference;
  onUpdate: (request: UpdatePreferencesRequest) => void;
  isUpdating: boolean;
}

const CategoryRow: React.FC<CategoryRowProps> = ({
  preference,
  onUpdate,
  isUpdating,
}) => {
  const config = NOTIFICATION_CATEGORY_CONFIG[preference.category];

  const handleToggle = (
    channel: 'emailEnabled' | 'pushEnabled' | 'smsEnabled',
    value: boolean
  ) => {
    onUpdate({
      category: preference.category,
      [channel]: value,
    });
  };

  return (
    <div className="flex items-center justify-between py-4 border-b border-warm-gray/10 last:border-0">
      <div className="flex-1">
        <h4 className="text-sm font-medium text-navy">{config.label}</h4>
        <p className="text-xs text-warm-gray mt-0.5">{config.description}</p>
      </div>

      <div className="flex items-center gap-6">
        {/* Email toggle */}
        <div className="flex flex-col items-center gap-1">
          <Toggle
            enabled={preference.emailEnabled}
            onChange={(v) => handleToggle('emailEnabled', v)}
            disabled={isUpdating}
            size="sm"
          />
          <span className="text-[10px] text-warm-gray">Email</span>
        </div>

        {/* Push toggle */}
        <div className="flex flex-col items-center gap-1">
          <Toggle
            enabled={preference.pushEnabled}
            onChange={(v) => handleToggle('pushEnabled', v)}
            disabled={isUpdating}
            size="sm"
          />
          <span className="text-[10px] text-warm-gray">Push</span>
        </div>

        {/* SMS toggle */}
        <div className="flex flex-col items-center gap-1">
          <Toggle
            enabled={preference.smsEnabled}
            onChange={(v) => handleToggle('smsEnabled', v)}
            disabled={isUpdating}
            size="sm"
          />
          <span className="text-[10px] text-warm-gray">SMS</span>
        </div>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------------------

export const NotificationPreferences: React.FC<NotificationPreferencesProps> = ({
  preferences,
  globalSettings,
  onUpdatePreference,
  onUpdateGlobalSettings,
  loading = false,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [localSettings, setLocalSettings] = useState(globalSettings);

  // Handle preference update
  const handleUpdatePreference = async (request: UpdatePreferencesRequest) => {
    setIsUpdating(true);
    try {
      await onUpdatePreference(request);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle global settings update
  const handleGlobalToggle = async (
    key: keyof GlobalNotificationSettings,
    value: boolean | string
  ) => {
    setLocalSettings((prev) => prev ? { ...prev, [key]: value } : prev);
    
    setIsUpdating(true);
    try {
      await onUpdateGlobalSettings({ [key]: value } as UpdateGlobalSettingsRequest);
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-sage animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Global Settings */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Bell className="w-5 h-5 text-sage" />
          <h3 className="text-lg font-semibold text-navy">Global Settings</h3>
        </div>

        <div className="bg-white rounded-xl border border-warm-gray/10 p-6 space-y-6">
          {/* Master toggles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Email notifications */}
            <div className="flex items-center justify-between p-4 bg-cream rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-sage/15 rounded-lg">
                  <Mail className="w-4 h-4 text-sage" />
                </div>
                <div>
                  <p className="text-sm font-medium text-navy">Email</p>
                  <p className="text-xs text-warm-gray">Receive email notifications</p>
                </div>
              </div>
              <Toggle
                enabled={localSettings?.emailEnabled ?? true}
                onChange={(v) => handleGlobalToggle('emailEnabled', v)}
                disabled={isUpdating}
              />
            </div>

            {/* Push notifications */}
            <div className="flex items-center justify-between p-4 bg-cream rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-terracotta/15 rounded-lg">
                  <Smartphone className="w-4 h-4 text-terracotta" />
                </div>
                <div>
                  <p className="text-sm font-medium text-navy">Push</p>
                  <p className="text-xs text-warm-gray">Browser notifications</p>
                </div>
              </div>
              <Toggle
                enabled={localSettings?.pushEnabled ?? true}
                onChange={(v) => handleGlobalToggle('pushEnabled', v)}
                disabled={isUpdating}
              />
            </div>

            {/* SMS notifications */}
            <div className="flex items-center justify-between p-4 bg-cream rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-navy/10 rounded-lg">
                  <MessageSquare className="w-4 h-4 text-navy" />
                </div>
                <div>
                  <p className="text-sm font-medium text-navy">SMS</p>
                  <p className="text-xs text-warm-gray">Text messages</p>
                </div>
              </div>
              <Toggle
                enabled={localSettings?.smsEnabled ?? false}
                onChange={(v) => handleGlobalToggle('smsEnabled', v)}
                disabled={isUpdating}
              />
            </div>
          </div>

          {/* Quiet Hours */}
          <div className="border-t border-warm-gray/10 pt-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-navy/10 rounded-lg">
                  <Moon className="w-4 h-4 text-navy" />
                </div>
                <div>
                  <p className="text-sm font-medium text-navy">Quiet Hours</p>
                  <p className="text-xs text-warm-gray">
                    Pause notifications during specific hours
                  </p>
                </div>
              </div>
              <Toggle
                enabled={localSettings?.quietHoursEnabled ?? false}
                onChange={(v) => handleGlobalToggle('quietHoursEnabled', v)}
                disabled={isUpdating}
              />
            </div>

            {localSettings?.quietHoursEnabled && (
              <div className="mt-4 ml-12 flex items-center gap-4">
                <div>
                  <label className="text-xs text-warm-gray">From</label>
                  <input
                    type="time"
                    value={localSettings.quietHoursStart || '22:00'}
                    onChange={(e) =>
                      handleGlobalToggle('quietHoursStart', e.target.value)
                    }
                    className="block mt-1 px-3 py-2 text-sm border border-warm-gray/20 rounded-lg focus:border-sage focus:ring-1 focus:ring-sage"
                  />
                </div>
                <div>
                  <label className="text-xs text-warm-gray">To</label>
                  <input
                    type="time"
                    value={localSettings.quietHoursEnd || '07:00'}
                    onChange={(e) =>
                      handleGlobalToggle('quietHoursEnd', e.target.value)
                    }
                    className="block mt-1 px-3 py-2 text-sm border border-warm-gray/20 rounded-lg focus:border-sage focus:ring-1 focus:ring-sage"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Category Preferences */}
      <section>
        <h3 className="text-lg font-semibold text-navy mb-4">
          Notification Categories
        </h3>
        <p className="text-sm text-warm-gray mb-4">
          Choose how you want to be notified for each category
        </p>

        <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
          {/* Header row */}
          <div className="flex items-center justify-between pb-3 border-b border-warm-gray/10 mb-2">
            <span className="text-xs font-medium text-warm-gray uppercase tracking-wider">
              Category
            </span>
            <div className="flex items-center gap-6">
              <span className="w-10 text-center text-xs font-medium text-warm-gray uppercase tracking-wider">
                Email
              </span>
              <span className="w-10 text-center text-xs font-medium text-warm-gray uppercase tracking-wider">
                Push
              </span>
              <span className="w-10 text-center text-xs font-medium text-warm-gray uppercase tracking-wider">
                SMS
              </span>
            </div>
          </div>

          {/* Category rows */}
          {preferences.map((pref) => (
            <CategoryRow
              key={pref.category}
              preference={pref}
              onUpdate={handleUpdatePreference}
              isUpdating={isUpdating}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

export default NotificationPreferences;

